import { Image } from 'expo-image';
import { Button } from 'components/Button';
import { Icon, ICONS } from 'components/Icon';
import { Txt } from 'components/Txt';
import { ProgressBar } from 'components/Visuals/ProgressBar';
import { theme } from 'lib/theme';
import { formatFileSize, isPDFFileType, isVideoFileType } from 'service/file';
import React from 'react';
import { StyleProp, StyleSheet, View, ViewStyle } from 'react-native';
import { useDispatch } from 'react-redux';
import { useSelector } from 'hooks/useSelector';
import { removeFileUpload } from 'store/fileUpload/actions';
import { selectFileUpload } from 'store/fileUpload/selectors';
import type { FileUploadStatus } from 'store/fileUpload/types';

function getProgressPercent({
  uploadedBytes,
  totalBytes,
}: {
  uploadedBytes: number;
  totalBytes: number;
}): number {
  return uploadedBytes > 0 ? Math.round((uploadedBytes / totalBytes) * 100) : 0;
}

const getUploadStatus = ({
  status,
  uploadedBytes,
  totalBytes,
}: {
  status: FileUploadStatus;
  uploadedBytes: number;
  totalBytes: number;
}): { text: string; color: string | null } | null => {
  switch (status) {
    case 'queued':
      return { text: 'Queued', color: null };
    case 'preparing':
      return { text: 'Preparing', color: null };
    case 'uploading':
      return {
        text: `Uploading ${getProgressPercent({
          uploadedBytes,
          totalBytes,
        })}%`,
        color: null,
      };
    case 'error':
      return { text: 'Failed', color: theme.colors.red };
    default:
      return null;
  }
};

export const FileUploadItem: React.FC<{
  uploadId: string;
  onStatusChange?: (status: FileUploadStatus) => void;
  onCancelled?: (uploadId: string) => void;
  cancelDisabled?: boolean;
  containerStyle?: StyleProp<ViewStyle>;
  thumbnailStyle?: StyleProp<ViewStyle>;
}> = ({
  uploadId,
  onStatusChange,
  onCancelled,
  cancelDisabled = false,
  containerStyle,
  thumbnailStyle,
}) => {
  const dispatch = useDispatch();
  const upload = useSelector((state) => selectFileUpload(state, uploadId));
  const previousStatus = React.useRef<FileUploadStatus | undefined>(undefined);

  React.useEffect(() => {
    if (upload && upload.status !== previousStatus.current) {
      previousStatus.current = upload.status;
      onStatusChange?.(upload.status);
    }
  }, [onStatusChange, upload]);

  const handleCancel = React.useCallback(() => {
    dispatch(removeFileUpload({ uploadId }));
    onCancelled?.(uploadId);
  }, [dispatch, onCancelled, uploadId]);

  if (!upload) {
    return null;
  }

  const {
    contentType,
    filename,
    previewUri,
    status,
    totalBytes,
    uploadedBytes,
  } = upload;
  const uploadStatus = getUploadStatus({
    status,
    totalBytes,
    uploadedBytes,
  });

  return (
    <View style={[styles.container, containerStyle]}>
      <View
        style={[
          styles.thumbnail,
          !previewUri && styles.thumbnailFallback,
          thumbnailStyle,
        ]}
      >
        {!['uploaded', 'failed'].includes(status) && (
          <View
            style={{
              position: 'absolute',
              bottom: 4,
              left: 4,
              right: 4,
              zIndex: 4,
            }}
          >
            <ProgressBar
              progress={getProgressPercent({
                totalBytes,
                uploadedBytes,
              })}
              accessibilityLabel={`${filename} upload progress`}
              height={5}
              containerStyle={{
                backgroundColor: theme.colors.white80,
                padding: 1,
              }}
            ></ProgressBar>
          </View>
        )}
        {!!previewUri ? (
          <Image source={{ uri: previewUri }} style={styles.thumbnailImage} />
        ) : isVideoFileType(filename, contentType) ? (
          <Icon source={ICONS.VideoCircleLight} color={theme.colors.white} />
        ) : isPDFFileType(filename, contentType) ? (
          <Icon source={ICONS.PDFLight} color={theme.colors.white} />
        ) : (
          <Icon source={ICONS.PaperClipLight} color={theme.colors.white} />
        )}
      </View>
      <View style={styles.detailsRow}>
        <View style={{ flex: 1 }}>
          <Txt size="xsmall" numberOfLines={uploadStatus ? 1 : 2}>
            {filename}
          </Txt>
          {!!uploadStatus && (
            <Txt
              size="xsmall"
              numberOfLines={1}
              weight="bold"
              style={
                uploadStatus.color ? { color: uploadStatus.color } : undefined
              }
            >
              {uploadStatus.text}
            </Txt>
          )}
        </View>
        <Txt
          style={{ color: theme.colors.textLight }}
          size="xsmall"
          numberOfLines={1}
        >
          {formatFileSize(totalBytes)}
        </Txt>
      </View>
      <Button
        icon={ICONS.XmarkLight}
        iconColor={theme.colors.red}
        destructive={true}
        onPress={handleCancel}
        disabled={cancelDisabled}
        accessibilityLabel={`Remove ${filename}`}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.padding / 2,
  },
  thumbnail: {
    position: 'relative',
    alignItems: 'center',
    backgroundColor: theme.colors.white,
    borderRadius: theme.cardBorderRadius,
    justifyContent: 'center',
    overflow: 'hidden',
    height: 40,
    width: 40,
    marginRight: theme.padding / 2,
  },
  thumbnailFallback: {
    backgroundColor: theme.colors.black30,
  },
  thumbnailImage: {
    height: '100%',
    width: '100%',
  },
  detailsRow: {
    flex: 1,
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.padding / 2,
  },
});
